import { useEffect, useRef, useState } from 'react';

import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input, Label, Toast } from '@repo/videa-components/src/index';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useConfigurePractice,
  useGetPracticeConfiguration,
} from '@/react-query/rcm/usePracticeConfiguration';
import routes from '@/routes/routes.constants';
import {
  isValidEmail,
  isValidTaxIdentificationNumber,
  isValidZipcode,
} from '@/utils/validation';

export const PracticeConfigurationSettingsPage = () => {
  const history = useHistory();
  const { t } = useLingui();
  const { data: configuration, isLoading } = useGetPracticeConfiguration();
  const configureMutation = useConfigurePractice();

  const [formData, setFormData] = useState({
    forwardingEmail: '',
    taxIdentificationNumber: '',
    nationalProviderId: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
  });

  const [errors, setErrors] = useState<Record<string, string | null>>({});

  // Track the last configuration ID we've initialized from to prevent infinite loops
  const lastInitializedConfigId = useRef<number | null>(null);

  // Update form data when configuration loads
  // Use configuration.id as dependency to prevent infinite loops from object reference changes
  useEffect(() => {
    if (configuration && configuration.id !== lastInitializedConfigId.current) {
      lastInitializedConfigId.current = configuration.id;
      setFormData({
        forwardingEmail: configuration.forwardingEmail || '',
        taxIdentificationNumber: configuration.taxIdentificationNumber || '',
        nationalProviderId: configuration.nationalProviderId || '',
        address1: configuration.address1 || '',
        address2: configuration.address2 || '',
        city: configuration.city || '',
        state: configuration.state || '',
        zipcode: configuration.zipcode || '',
      });
    }
    // Intentionally only depend on configuration.id to prevent infinite loops
    // when React Query returns new object references with the same data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration?.id]);

  const validateField = (name: string, value: string): string | null => {
    switch (name) {
      case 'forwardingEmail':
        if (!value) {
          return t`Email is required`;
        }
        if (!isValidEmail(value)) {
          return t`Please enter a valid email address`;
        }
        return null;
      case 'taxIdentificationNumber':
        if (!value) {
          return t`Tax Identification Number is required`;
        }
        if (!isValidTaxIdentificationNumber(value)) {
          return t`Tax Identification Number must be in format 043336633 or 04-3336633`;
        }
        return null;
      case 'nationalProviderId':
        if (!value) {
          return t`National Provider ID is required`;
        }
        return null;
      case 'address1':
        if (!value) {
          return t`Address is required`;
        }
        return null;
      case 'address2':
        if (!value) {
          return t`Address Line 2 is required`;
        }
        return null;
      case 'city':
        if (!value) {
          return t`City is required`;
        }
        return null;
      case 'state':
        if (!value) {
          return t`State is required`;
        }
        return null;
      case 'zipcode':
        if (!value) {
          return t`Zipcode is required`;
        }
        if (!isValidZipcode(value)) {
          return t`Zipcode must be exactly 5 digits`;
        }
        return null;
      default:
        return null;
    }
  };

  const handleFieldChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleFieldBlur = (name: string) => {
    const error = validateField(name, formData[name as keyof typeof formData]);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string | null> = {};
    let isValid = true;

    (Object.keys(formData) as Array<keyof typeof formData>).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleCopyAddress1ToAddress2 = () => {
    setFormData((prev) => ({ ...prev, address2: prev.address1 }));
    // Clear any existing error for address2
    if (errors.address2) {
      setErrors((prev) => ({ ...prev, address2: null }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await configureMutation.mutateAsync({
        forwardingEmail: formData.forwardingEmail,
        taxIdentificationNumber: formData.taxIdentificationNumber,
        nationalProviderId: formData.nationalProviderId,
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        state: formData.state,
        zipcode: formData.zipcode,
      });
      toast(
        <Toast
          variant="success"
          title={t`Settings saved successfully`}
          description={t`Practice configuration has been updated.`}
        />,
      );
    } catch (error) {
      toast(
        <Toast
          variant="error"
          title={t`Failed to save settings`}
          description={t`Please try again.`}
        />,
      );
    }
  };

  const handleBack = () => {
    history.push(routes.CONNECT_PAYERS);
  };

  const isFormDirty = () => {
    // If no configuration exists yet, check if any form fields have values
    // This allows users to save their first configuration
    if (!configuration) {
      return Object.values(formData).some((value) => value.trim() !== '');
    }

    // If configuration exists, compare form data with existing configuration
    const configAddress2 = configuration.address2 || '';
    const formAddress2 = formData.address2 || '';
    return (
      formData.forwardingEmail !== (configuration.forwardingEmail || '') ||
      formData.taxIdentificationNumber !==
        (configuration.taxIdentificationNumber || '') ||
      formData.nationalProviderId !==
        (configuration.nationalProviderId || '') ||
      formData.address1 !== (configuration.address1 || '') ||
      formAddress2 !== configAddress2 ||
      formData.city !== (configuration.city || '') ||
      formData.state !== (configuration.state || '') ||
      formData.zipcode !== (configuration.zipcode || '')
    );
  };

  return (
    <div className="vai-flex vai-flex-col vai-h-screen vai-w-full vai-overflow-hidden">
      {/* Topbar */}
      <div className="vai-px-6 vai-py-5 vai-bg-white vai-border-b vai-text-primary vai-flex vai-justify-between vai-items-center vai-flex-shrink-0">
        <button
          onClick={handleBack}
          className="vai-flex vai-items-center vai-gap-2 vai-text-primary hover:vai-opacity-80 vai-transition-opacity"
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="vai-mr-2 vai-text-primary"
          />
          <span>
            <Trans>Back to Auto Verify Credentials</Trans>
          </span>
        </button>
      </div>

      {/* Page Content */}
      <div className="vai-flex-1 vai-overflow-auto vai-bg-gray-50 vai-flex vai-flex-col vai-min-h-0">
        <div className="vai-p-6 vai-flex-shrink-0">
          {/* Title Section */}
          <div>
            <h1 className="vai-text-2xl vai-font-semibold vai-text-gray-900 vai-mb-2">
              <Trans>Practice Configuration Settings</Trans>
            </h1>
            <p className="vai-text-sm vai-text-gray-600">
              <Trans>
                Manage your practice configuration settings for Auto Verify.
              </Trans>
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="vai-flex-1 vai-overflow-auto vai-px-6 vai-pb-6 vai-min-h-0">
          <div className="vai-max-w-2xl">
            <div className="vai-bg-white vai-rounded-lg vai-p-6 vai-border vai-border-border">
              {isLoading ? (
                <div className="vai-space-y-4">
                  <div className="vai-animate-pulse">
                    <div className="vai-h-4 vai-bg-gray-200 vai-rounded vai-w-1/4 vai-mb-2"></div>
                    <div className="vai-h-10 vai-bg-gray-200 vai-rounded"></div>
                  </div>
                </div>
              ) : (
                <div className="vai-space-y-6">
                  {/* Forwarding Email */}
                  <div className="vai-space-y-2">
                    <Label
                      htmlFor="forwardingEmail"
                      className="vai-text-sm vai-font-medium"
                    >
                      <Trans>Forwarding Email</Trans>
                      <span className="vai-text-red-500 vai-ml-1">*</span>
                    </Label>
                    <Input
                      id="forwardingEmail"
                      type="email"
                      value={formData.forwardingEmail}
                      onChange={(e) =>
                        handleFieldChange('forwardingEmail', e.target.value)
                      }
                      onBlur={() => handleFieldBlur('forwardingEmail')}
                      placeholder={t`Enter forwarding email address`}
                      className={`vai-h-10 ${
                        errors.forwardingEmail ? 'vai-border-red-500' : ''
                      }`}
                      disabled={configureMutation.isLoading}
                    />
                    {errors.forwardingEmail && (
                      <p className="vai-text-red-500 vai-text-xs vai-mt-1">
                        {errors.forwardingEmail}
                      </p>
                    )}
                    <p className="vai-text-xs vai-text-gray-500 vai-mt-1">
                      <Trans>
                        This email will be used for forwarding MFA codes.
                      </Trans>
                    </p>
                  </div>

                  {/* Tax Identification Number and National Provider ID */}
                  <div className="vai-grid vai-grid-cols-2 vai-gap-4">
                    <div className="vai-space-y-2">
                      <Label
                        htmlFor="taxIdentificationNumber"
                        className="vai-text-sm vai-font-medium"
                      >
                        <Trans>Tax Identification Number</Trans>
                        <span className="vai-text-red-500 vai-ml-1">*</span>
                      </Label>
                      <Input
                        id="taxIdentificationNumber"
                        type="text"
                        value={formData.taxIdentificationNumber}
                        onChange={(e) =>
                          handleFieldChange(
                            'taxIdentificationNumber',
                            e.target.value,
                          )
                        }
                        onBlur={() =>
                          handleFieldBlur('taxIdentificationNumber')
                        }
                        placeholder={t`Enter tax identification number`}
                        className={`vai-h-10 ${
                          errors.taxIdentificationNumber
                            ? 'vai-border-red-500'
                            : ''
                        }`}
                        disabled={configureMutation.isLoading}
                      />
                      {errors.taxIdentificationNumber && (
                        <p className="vai-text-red-500 vai-text-xs vai-mt-1">
                          {errors.taxIdentificationNumber}
                        </p>
                      )}
                    </div>

                    <div className="vai-space-y-2">
                      <Label
                        htmlFor="nationalProviderId"
                        className="vai-text-sm vai-font-medium"
                      >
                        <Trans>National Provider ID</Trans>
                        <span className="vai-text-red-500 vai-ml-1">*</span>
                      </Label>
                      <Input
                        id="nationalProviderId"
                        type="text"
                        value={formData.nationalProviderId}
                        onChange={(e) =>
                          handleFieldChange(
                            'nationalProviderId',
                            e.target.value,
                          )
                        }
                        onBlur={() => handleFieldBlur('nationalProviderId')}
                        placeholder={t`Enter national provider ID`}
                        className={`vai-h-10 ${
                          errors.nationalProviderId ? 'vai-border-red-500' : ''
                        }`}
                        disabled={configureMutation.isLoading}
                      />
                      {errors.nationalProviderId && (
                        <p className="vai-text-red-500 vai-text-xs vai-mt-1">
                          {errors.nationalProviderId}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Address Line 1 */}
                  <div className="vai-space-y-2">
                    <Label
                      htmlFor="address1"
                      className="vai-text-sm vai-font-medium"
                    >
                      <Trans>Address Line 1</Trans>
                      <span className="vai-text-red-500 vai-ml-1">*</span>
                    </Label>
                    <Input
                      id="address1"
                      type="text"
                      value={formData.address1}
                      onChange={(e) =>
                        handleFieldChange('address1', e.target.value)
                      }
                      onBlur={() => handleFieldBlur('address1')}
                      placeholder={t`Enter address line 1`}
                      className={`vai-h-10 ${
                        errors.address1 ? 'vai-border-red-500' : ''
                      }`}
                      disabled={configureMutation.isLoading}
                    />
                    {errors.address1 && (
                      <p className="vai-text-red-500 vai-text-xs vai-mt-1">
                        {errors.address1}
                      </p>
                    )}
                  </div>

                  {/* Address Line 2 */}
                  <div className="vai-space-y-2">
                    <div className="vai-flex vai-items-center vai-justify-between">
                      <Label
                        htmlFor="address2"
                        className="vai-text-sm vai-font-medium"
                      >
                        <Trans>Address Line 2</Trans>
                        <span className="vai-text-red-500 vai-ml-1">*</span>
                      </Label>
                      {formData.address1 && (
                        <button
                          type="button"
                          onClick={handleCopyAddress1ToAddress2}
                          className="vai-text-sm vai-text-blue-600 hover:vai-text-blue-800 vai-underline vai-cursor-pointer"
                          disabled={configureMutation.isLoading}
                        >
                          <Trans>Copy from Address Line 1</Trans>
                        </button>
                      )}
                    </div>
                    <Input
                      id="address2"
                      type="text"
                      value={formData.address2}
                      onChange={(e) =>
                        handleFieldChange('address2', e.target.value)
                      }
                      onBlur={() => handleFieldBlur('address2')}
                      placeholder={t`Enter address line 2`}
                      className={`vai-h-10 ${
                        errors.address2 ? 'vai-border-red-500' : ''
                      }`}
                      disabled={configureMutation.isLoading}
                    />
                    {errors.address2 && (
                      <p className="vai-text-red-500 vai-text-xs vai-mt-1">
                        {errors.address2}
                      </p>
                    )}
                    <p className="vai-text-xs vai-text-gray-500 vai-mt-1">
                      <Trans>
                        If you don&apos;t have a value for address 2, copy it
                        from address 1.
                      </Trans>
                    </p>
                  </div>

                  {/* City, State, Zipcode */}
                  <div className="vai-grid vai-grid-cols-3 vai-gap-4">
                    <div className="vai-space-y-2">
                      <Label
                        htmlFor="city"
                        className="vai-text-sm vai-font-medium"
                      >
                        <Trans>City</Trans>
                        <span className="vai-text-red-500 vai-ml-1">*</span>
                      </Label>
                      <Input
                        id="city"
                        type="text"
                        value={formData.city}
                        onChange={(e) =>
                          handleFieldChange('city', e.target.value)
                        }
                        onBlur={() => handleFieldBlur('city')}
                        placeholder={t`Enter city`}
                        className={`vai-h-10 ${
                          errors.city ? 'vai-border-red-500' : ''
                        }`}
                        disabled={configureMutation.isLoading}
                      />
                      {errors.city && (
                        <p className="vai-text-red-500 vai-text-xs vai-mt-1">
                          {errors.city}
                        </p>
                      )}
                    </div>

                    <div className="vai-space-y-2">
                      <Label
                        htmlFor="state"
                        className="vai-text-sm vai-font-medium"
                      >
                        <Trans>State</Trans>
                        <span className="vai-text-red-500 vai-ml-1">*</span>
                      </Label>
                      <Input
                        id="state"
                        type="text"
                        value={formData.state}
                        onChange={(e) =>
                          handleFieldChange('state', e.target.value)
                        }
                        onBlur={() => handleFieldBlur('state')}
                        placeholder={t`Enter state`}
                        className={`vai-h-10 ${
                          errors.state ? 'vai-border-red-500' : ''
                        }`}
                        disabled={configureMutation.isLoading}
                      />
                      {errors.state && (
                        <p className="vai-text-red-500 vai-text-xs vai-mt-1">
                          {errors.state}
                        </p>
                      )}
                    </div>

                    <div className="vai-space-y-2">
                      <Label
                        htmlFor="zipcode"
                        className="vai-text-sm vai-font-medium"
                      >
                        <Trans>Zipcode</Trans>
                        <span className="vai-text-red-500 vai-ml-1">*</span>
                      </Label>
                      <Input
                        id="zipcode"
                        type="text"
                        value={formData.zipcode}
                        onChange={(e) =>
                          handleFieldChange('zipcode', e.target.value)
                        }
                        onBlur={() => handleFieldBlur('zipcode')}
                        placeholder={t`Enter zipcode`}
                        className={`vai-h-10 ${
                          errors.zipcode ? 'vai-border-red-500' : ''
                        }`}
                        disabled={configureMutation.isLoading}
                      />
                      {errors.zipcode && (
                        <p className="vai-text-red-500 vai-text-xs vai-mt-1">
                          {errors.zipcode}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="vai-flex vai-justify-end vai-pt-2">
                    <Button
                      onClick={handleSave}
                      disabled={
                        configureMutation.isLoading ||
                        !isFormDirty() ||
                        Object.values(errors).some((error) => error !== null)
                      }
                      className="vai-min-w-[120px]"
                    >
                      {configureMutation.isLoading ? (
                        <Trans>Saving...</Trans>
                      ) : (
                        <Trans>Save Changes</Trans>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
